// eslint-disable-next-line @typescript-eslint/ban-ts-comment -- pi-lens/Deno resolver false positives on zod/workspace package imports in this repo-native schema module; package toolchain resolves them correctly
// @ts-nocheck
/**
 * json-render Zod schemas for DramaDetector catalog components
 * Aligned with: analysis.ts, Winston (#3543/2), Sally (#3543/4)
 * Task: #783.S1 — [NocoDB #784] | Agent: Diego <diego@liftping.net>
 */

import { z } from "zod";

// Primitive schemas — aligned with packages/shared/src/types/analysis.ts
export const DramaCategorySchema = z.enum([
	"safe",
	"caution",
	"high_drama",
	"extreme_drama",
]);
export const SubscriptionTierSchema = z.enum(["free", "drama_plus", "dramax"]);
export const IssueSeveritySchema = z.enum([
	"low",
	"medium",
	"high",
	"critical",
]);

// DDRuntimeStateSchema — injected by server for tier enforcement

export const DDRuntimeStateSchema = z.object({
	/** Server-verified subscription tier — MUST come from useTier(), never from spec */
	tier: SubscriptionTierSchema,
	/** Authenticated user ID */
	userId: z.string(),
	/** The analysisId being rendered */
	analysisId: z.string(),
	/** Drama category of the current analysis */
	category: DramaCategorySchema.optional(),
	/** Raw drama score (0–10) */
	score: z.number().min(0).max(10).optional(),
	/** Module readiness data for progressive disclosure components */
	moduleReadiness: z
		.record(
			z.string(),
			z.object({
				confidence: z.enum(["none", "low", "medium", "high"]),
				current: z.number(),
				required: z.number(),
			}),
		)
		.optional(),
});

export type DDRuntimeState = z.infer<typeof DDRuntimeStateSchema>;

// Component prop schemas (13 catalog components)
// Source of truth: Sally's catalog spec (Discourse #3543/4)

/**
 * 1. AnalysisCard — Free+
 * Primary container card for an analysis section.
 * Maps to: drama-card.tsx
 */
export const AnalysisCardPropsSchema = z.object({
	title: z.string().max(60),
	subtitle: z.string().max(80).optional(),
	variant: z.enum(["standard", "elevated", "premium"]),
	tierHint: SubscriptionTierSchema.optional(),
	children: z.array(z.unknown()).optional(),
});

export type AnalysisCardProps = z.infer<typeof AnalysisCardPropsSchema>;

/**
 * 2. DramaScore — Free+
 * Hero element. Always first visible element in a result.
 * Maps to: drama-score.tsx (NOT the animated DramaScoreGauge)
 */
export const DramaScorePropsSchema = z.object({
	score: z.number().min(0).max(10),
	category: DramaCategorySchema,
	size: z.enum(["sm", "md", "lg"]).default("md"),
	showLabel: z.boolean().default(true),
});

export type DramaScoreProps = z.infer<typeof DramaScorePropsSchema>;

/**
 * 3. DramaCategoryBadge — Free+
 * Compact badge showing drama category.
 * Maps to: drama-badge.tsx
 */
export const DramaCategoryBadgePropsSchema = z.object({
	category: DramaCategorySchema,
	size: z.enum(["sm", "md", "lg"]).default("md"),
	showIcon: z.boolean().default(true),
});

export type DramaCategoryBadgeProps = z.infer<
	typeof DramaCategoryBadgePropsSchema
>;

/**
 * 4. NarrativeBlock — Free+
 * Prose content block for narrative analysis text.
 * Maps to: p + tailwind classes
 */
export const NarrativeBlockPropsSchema = z.object({
	text: z.string().max(2000),
	emphasis: z.enum(["normal", "high", "critical"]).default("normal"),
});

export type NarrativeBlockProps = z.infer<typeof NarrativeBlockPropsSchema>;

/**
 * 5. TierBadge — Free+
 * Displays subscription tier badge (for content attribution).
 * Maps to: TierCard.tsx
 */
export const TierBadgePropsSchema = z.object({
	tier: SubscriptionTierSchema,
	label: z.string().optional(),
	showUpgradeHint: z.boolean().default(false),
});

export type TierBadgeProps = z.infer<typeof TierBadgePropsSchema>;

/**
 * 6. InsightRow — Free+
 * Single insight item, typically within a list.
 * Maps to: Analytics/InsightCard.tsx
 */
export const InsightRowPropsSchema = z.object({
	title: z.string().max(100),
	description: z.string().max(300),
	trend: z.enum(["up", "down", "neutral"]).optional(),
	changePercent: z.number().optional(),
});

export type InsightRowProps = z.infer<typeof InsightRowPropsSchema>;

/**
 * 7. SectionDivider — Free+
 * Horizontal rule / visual separator between sections.
 * Maps to: hr + tailwind
 */
export const SectionDividerPropsSchema = z.object({
	label: z.string().max(60).optional(),
	variant: z.enum(["subtle", "emphasized"]).default("subtle"),
	spacing: z.enum(["sm", "md", "lg"]).default("md"),
});

export type SectionDividerProps = z.infer<typeof SectionDividerPropsSchema>;

/**
 * 8. ActionButton — Free+
 * CTA button wired to catalog actions.
 * Maps to: TierButton.tsx
 */
export const ActionButtonPropsSchema = z.object({
	label: z.string().max(60),
	action: z.string(),
	tier: SubscriptionTierSchema.default("free"),
	variant: z
		.enum(["primary", "secondary", "ghost", "upgrade"])
		.default("primary"),
	disabled: z.boolean().default(false),
});

export type ActionButtonProps = z.infer<typeof ActionButtonPropsSchema>;

/**
 * 9. PatternSpotlight — Drama+ only (tier-gated)
 * Premium pattern analysis card. Requires drama_plus or dramax tier.
 * Maps to: PatternSpotlightCard.tsx
 */
export const PatternSpotlightPropsSchema = z.object({
	patternName: z.string().max(80),
	description: z.string().max(500),
	severity: IssueSeveritySchema,
	occurrences: z.number().int().min(1),
	examples: z.array(z.string().max(300)).max(3).optional(),
	suggestion: z.string().max(400).optional(),
});

export type PatternSpotlightProps = z.infer<typeof PatternSpotlightPropsSchema>;

/**
 * 10. TransformationInsight — DramaX only (tier-gated)
 * Premium editorial before/after transformation card. Requires dramax tier.
 * Maps to: TransformationInsightCard.tsx
 */
export const TransformationInsightPropsSchema = z.object({
	title: z.string().max(80),
	before: z.string().max(600),
	after: z.string().max(600),
	rationale: z.string().max(400),
	category: DramaCategorySchema,
	impactScore: z.number().min(0).max(10),
});

export type TransformationInsightProps = z.infer<
	typeof TransformationInsightPropsSchema
>;

/**
 * 11. StyleConsistencyInsight — Drama+
 * Communication style analysis with formality/vocabulary metrics.
 * Maps to: analysis-results/StyleConsistencySection.tsx
 */
export const StyleConsistencyInsightPropsSchema = z.object({
	formalityShift: z.number().min(0).max(1),
	vocabularyDiversity: z.number().min(0).max(1),
	punctuationPattern: z.enum(["informal", "mixed", "formal"]),
	messageLengthCategory: z.enum(["brief", "moderate", "lengthy"]),
	flags: z.array(z.string()).default([]),
});

export type StyleConsistencyInsightProps = z.infer<
	typeof StyleConsistencyInsightPropsSchema
>;

/**
 * 12. CommunicationPatternInsight — Drama+
 * Pattern shift detection with confidence and contributing signals.
 * Maps to: analysis-results/AiDetectionSection.tsx
 * CRITICAL: Never use "AI" in description. Use "Communication Pattern".
 */
export const CommunicationPatternInsightPropsSchema = z.object({
	aiLikelihood: z.number().min(0).max(1),
	label: z.enum(["natural", "mixed", "likely_assisted"]),
	hasBaseline: z.boolean(),
	signals: z
		.array(
			z.object({
				name: z.string(),
				score: z.number().min(0).max(1),
				weight: z.number().min(0).max(1),
			}),
		)
		.default([]),
});

export type CommunicationPatternInsightProps = z.infer<
	typeof CommunicationPatternInsightPropsSchema
>;

/**
 * 13. ModuleProgress — Free+
 * Progress indicator for modules building baseline data.
 * Maps to: analysis-results/ModuleProgressCard.tsx
 */
export const ModuleProgressPropsSchema = z.object({
	moduleId: z.string(),
	moduleName: z.string().max(60),
	current: z.number().min(0),
	required: z.number().min(1),
	confidence: z.enum(["none", "low", "medium", "high"]).default("none"),
	isPremium: z.boolean().default(true),
});

export type ModuleProgressProps = z.infer<typeof ModuleProgressPropsSchema>;

// Catalog action schemas
export const UpgradeTierParamsSchema = z.object({
	targetTier: SubscriptionTierSchema,
	featureName: z.string().optional(),
});

export const ShareResultParamsSchema = z.object({
	analysisId: z.string(),
	channel: z.enum(["clipboard", "native"]).default("clipboard"),
});

export const SaveAnalysisParamsSchema = z.object({
	analysisId: z.string(),
});

export const DismissInsightParamsSchema = z.object({
	insightId: z.string(),
});

// $computed expression schemas — extracted to ./computed.ts for file size (#2084)
export {
	DDComputedFunctionNames,
	ComputedExpressionSchema,
	StringOrComputedSchema,
} from "./computed";
export type {
	DDComputedFunctionName,
	ComputedExpression,
} from "./computed";

// Union exports for convenience
export const ComponentPropSchemas = {
	AnalysisCard: AnalysisCardPropsSchema,
	DramaScore: DramaScorePropsSchema,
	DramaCategoryBadge: DramaCategoryBadgePropsSchema,
	NarrativeBlock: NarrativeBlockPropsSchema,
	TierBadge: TierBadgePropsSchema,
	InsightRow: InsightRowPropsSchema,
	SectionDivider: SectionDividerPropsSchema,
	ActionButton: ActionButtonPropsSchema,
	PatternSpotlight: PatternSpotlightPropsSchema,
	TransformationInsight: TransformationInsightPropsSchema,
	StyleConsistencyInsight: StyleConsistencyInsightPropsSchema,
	CommunicationPatternInsight: CommunicationPatternInsightPropsSchema,
	ModuleProgress: ModuleProgressPropsSchema,
} as const;

export const ActionParamSchemas = {
	upgrade_tier: UpgradeTierParamsSchema,
	share_result: ShareResultParamsSchema,
	save_analysis: SaveAnalysisParamsSchema,
	dismiss_insight: DismissInsightParamsSchema,
} as const;

// ============================================================================
// SDUI surface config schemas
// ============================================================================

export const SDUIBlurConfigSchema = z.object({
	blurAmountLight: z.number(),
	blurAmountHeavy: z.number(),
	teaserCopy: z.string().nullable(),
	ctaCopy: z.string().nullable(),
	patternCountThreshold: z.number().nullable(),
	issueCountThreshold: z.number().nullable(),
});

export const SDUIScoreDeltaConfigSchema = z.object({
	displayFormat: z.enum(["delta", "arrow", "percentage"]),
	showSign: z.boolean(),
	showArrow: z.boolean(),
});

export const SDUIConsentModalConfigSchema = z.object({
	enabled: z.boolean(),
	analysisThreshold: z.number(),
	inlineBannerThreshold: z.number(),
	blurOverlayThreshold: z.number(),
	bottomSheetThreshold: z.number(),
	dismissCooldown: z.number(),
	dismissable: z.boolean(),
});

export const SDUIReferralShareConfigSchema = z.object({
	shareFooter: z.string().nullable(),
	shareCtaCopy: z.string().nullable(),
});

export const SDUISubscriptionPageConfigSchema = z.object({
	showDramaxPlans: z.boolean(),
	showFamilyPlans: z.boolean(),
	checkoutEnabled: z.boolean(),
});

export const SDUIOGImageConfigSchema = z.object({
	showScore: z.boolean(),
	showSummary: z.boolean(),
	maxSummaryLength: z.number(),
	footerText: z.string().nullable(),
});

export const SDUIShareModalFormatOptionSchema = z.object({
	id: z.enum(["portrait", "square", "reel", "horizontal"]),
	label: z.string(),
	sublabel: z.string(),
	hint: z.string(),
	pw: z.number(),
	ph: z.number(),
});

export const SDUIShareModalConfigSchema = z.object({
	availableFormats: z.array(SDUIShareModalFormatOptionSchema),
	defaultFormat: z.enum(["portrait", "square", "reel", "horizontal"]),
	shareModalTone: z.enum(["playful", "clinical", "editorial"]),
	templateOrdering: z.enum(["narrator-first", "expert-first"]),
	showFormatHint: z.boolean(),
	showPreview: z.boolean(),
	shareFooter: z.string().nullable(),
});

export const SDUIHabitEngineConfigSchema = z.object({
	streakRewardThreshold: z.number(),
	questUnlockAnalysisCount: z.number(),
	celebrationType: z.enum(["confetti", "badge", "both"]),
	streakResetGracePeriodHours: z.number(),
	dailyQuestCount: z.number(),
});

export const SDUIDramaCardsConfigSchema = z.object({
	cardsPerSession: z.number(),
	deckUnlockThreshold: z.number(),
	showDifficultyBadge: z.boolean(),
	sessionCooldownMinutes: z.number(),
	maxDailySessionsFree: z.number(),
	maxDailySessionsPremium: z.number(),
});

export const SDUISurfaceConfigSchemas = {
	"blur-teaser": SDUIBlurConfigSchema,
	"score-delta": SDUIScoreDeltaConfigSchema,
	"consent-modal": SDUIConsentModalConfigSchema,
	"referral-share": SDUIReferralShareConfigSchema,
	"subscription-page": SDUISubscriptionPageConfigSchema,
	"og-image": SDUIOGImageConfigSchema,
	"share-modal-config": SDUIShareModalConfigSchema,
	"habit-engine": SDUIHabitEngineConfigSchema,
	dramacards: SDUIDramaCardsConfigSchema,
} as const;

export function sanitizeSduiSurfaceConfig(
	surfaceKey: keyof typeof SDUISurfaceConfigSchemas,
	value: unknown,
): Record<string, unknown> | null {
	if (!value || typeof value !== "object" || Array.isArray(value)) {
		return null;
	}

	const schema = SDUISurfaceConfigSchemas[surfaceKey];
	const shape = schema.shape;
	const sanitized: Record<string, unknown> = {};
	let hasValidField = false;

	for (const [field, fieldSchema] of Object.entries(shape)) {
		const candidate = (value as Record<string, unknown>)[field];
		if (candidate === undefined) {
			continue;
		}
		const parsed = (fieldSchema as z.ZodTypeAny).safeParse(candidate);
		if (!parsed.success) {
			continue;
		}
		sanitized[field] = parsed.data;
		hasValidField = true;
	}

	return hasValidField ? sanitized : null;
}
